import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import React, { Fragment, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';

import { deleteAccountUser } from '../../services/accountUser.service';
import { fetchProvidersList } from '../../services/provider.service';
import {
    customTableStyles,
    customTableStylesAccountView,
    dateTimeFormat,
} from '../../utils';
import NotifyAccount from '../account/notifyAccountManager.component';
import EditAccountUser from '../accountUser/accountUserEdit.component';
import DeleteConfirmation from '../deleteConfirmation.component';
import MessageContainer from '../message.component';
import NotifyConfirmation from '../notifyConfirmation.component';
import WithLogin from '../withLogin.component';
import ViewProvider from './providerView.component';

const ProviderList = props => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openViewForm, setOpenViewForm] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [totalRows, setTotalRows] = useState(0);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [message, setMessage] = useState({});
    const [openEditForm, setOpenEditForm] = useState(false);
    const [showNotifyConfirmation, setShowNotifyConfirmation] = useState(false);
    const [openNotifyForm, setOpenNotifyForm] = useState(false);
    const [accountPersonId, setAccountPersonId] = useState(false);

    const onEdit = row => {
        setSelectedRow(row);
        setOpenEditForm(true);
    };

    const onDelete = async row => {
        setShowDeleteConfirmation(true);
        setSelectedRow(row);
    };

    const onNotify = row => {
        setAccountPersonId(row.account_person_id);
        setShowNotifyConfirmation(true);
        setSelectedRow(row);
    };

    const notifyAccountUser = row => {
        setOpenNotifyForm(true);
        setSelectedRow(row);
    };

    const columns = [
        {
            name: 'id',
            selector: row => row.id,
            width: '8%',
        },
        {
            name: 'Account',
            selector: row => row.account_name,
            width: '15%',
        },
        {
            name: 'Name',
            selector: row => `${row.first_name} ${row.last_name}`,
            width: '15%',
        },
        {
            name: 'Email',
            selector: row => row.email,
            width: '15%',
        },
        {
            name: 'NPI ID',
            selector: row => row.npi_id,
        },
        {
            name: 'Site',
            selector: row => (
                <div>
                    {row.clinics.map((clinic, i) => (
                        <span key={i}>
                            {clinic.name}
                            {i !== row.clinics.length - 1 && <span>, </span>}
                        </span>
                    ))}
                </div>
            ),
        },
        {
            name: 'Active',
            selector: row => (row.is_active ? 'Yes' : 'No'),
            width: '5%',
        },
        {
            name: 'Created at',
            selector: row => dateTimeFormat(row.created_at),
        },

        {
            key: 'action',
            text: 'Action',
            name: 'Action',
            className: 'action',
            width: '130px',
            align: 'left',
            sortable: false,
            cell: row => {
                return (
                    <Fragment>
                        <span className="table-action-icon">
                            <EditIcon
                                className="icon-color"
                                onClick={() => {
                                    onEdit(row);
                                }}
                            />
                        </span>
                        &nbsp;
                        <VisibilityIcon
                            className="icon-color"
                            onClick={() => {
                                onView(row);
                            }}
                        />
                        &nbsp;
                        <DeleteIcon
                            color="red"
                            className="table-action-icon red"
                            onClick={() => onDelete(row)}
                        />
                        &nbsp;
                        {!row.is_activated_by_user && (
                            <Link
                                onClick={() => {
                                    onNotify(row);
                                }}
                                className="table-action-icon">
                                <NotificationsNoneIcon className="icon-color" />
                            </Link>
                        )}
                    </Fragment>
                );
            },
        },
    ];

    const onView = row => {
        setOpenViewForm(true);
        setSelectedRow(row);
    };

    const getProviders = async (toPage, toPerPage, searchText, accountId) => {
        setLoading(true);
        setMessage({});
        toPage = toPage ? toPage : page;
        toPerPage = toPerPage ? toPerPage : perPage;
        try {
            const response = await fetchProvidersList(
                toPage,
                toPerPage,
                searchText,
                accountId,
            );
            setData(response.items);
            setTotalRows(response.total);
        } catch (error) {
            setMessage({
                detail: 'An error has occurrded during data loading.',
                type: 'Error',
            });
        }
        setLoading(false);
    };

    useEffect(() => {
        getProviders(page, perPage, props.providerSearchText, props.accountId);
    }, [props.accountId, props.providerSearchText, props.reloadData]); // eslint-disable-line react-hooks/exhaustive-deps

    const deleteProvider = async id => {
        setMessage({});
        try {
            const resp = await deleteAccountUser(id);
            reloadDataTable(page);
            setMessage({
                type: 'Success',
                message: `${resp.detail}`,
            });
        } catch (error) {
            if (
                error.response.status === 400 ||
                error.response.status === 404
            ) {
                setMessage({
                    type: 'Error',
                    message: `${error.response.data.detail}`,
                });
            }
        }
        setShowDeleteConfirmation(false);
    };

    const handlePageChange = toPage => {
        getProviders(page);
        setPage(toPage);
    };
    const reloadDataTable = (toPage, perPage) => {
        getProviders(
            toPage,
            perPage,
            props.providerSearchText,
            props.accountId,
        );
        if (toPage) {
            setPage(toPage);
        }
    };

    const handlePerRowsChange = async (newPerPage, toPage) => {
        setLoading(true);
        setMessage({});
        await getProviders(toPage, newPerPage);
        setPerPage(newPerPage);
        setLoading(false);
    };

    return (
        <>
            {openEditForm && (
                <EditAccountUser
                    open={openEditForm}
                    setEditForm={setOpenEditForm}
                    selectedRow={selectedRow}
                    reloadDataTable={reloadDataTable}
                    accountPersonId={selectedRow.account_person_id}
                />
            )}
            {openViewForm && (
                <ViewProvider
                    open={openViewForm}
                    setEditForm={setOpenViewForm}
                    selectedRow={selectedRow}
                    reloadDataTable={reloadDataTable}
                />
            )}
            {openNotifyForm && (
                <NotifyAccount
                    open={openNotifyForm}
                    setNotifyForm={setOpenNotifyForm}
                    accountPersonId={accountPersonId}
                    reloadDataTable={reloadDataTable}
                />
            )}
            <span
                color="primary"
                className="float-end"
                style={{ marginRight: '10px' }}
                onClick={() => reloadDataTable()}>
                <SyncIcon />
            </span>
            <div>
                <div className="new-line"></div>
                {message && <MessageContainer message={message} />}
                <div className="accounts-list-table">
                    <DataTable
                        customStyles={
                            props.accountId
                                ? customTableStylesAccountView
                                : customTableStyles
                        }
                        width="100%"
                        columns={columns}
                        data={data}
                        progressPending={loading}
                        pagination
                        paginationServer
                        paginationDefaultPage={page}
                        paginationResetDefaultPage={true}
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                    />
                </div>
                <DeleteConfirmation
                    showModal={showDeleteConfirmation}
                    hideModal={() => setShowDeleteConfirmation(false)}
                    confirmModal={deleteProvider}
                    deleteId={selectedRow.account_person_id}
                    message="Are you sure you want to delete it"
                />
                {showNotifyConfirmation && (
                    <NotifyConfirmation
                        showModal={showNotifyConfirmation}
                        hideModal={() => setShowNotifyConfirmation(false)}
                        confirmModal={notifyAccountUser}
                        buttonName="Send"
                        notifyAccountId={selectedRow}
                        message={`Do you want to resend welcome email with login credential to ${selectedRow.first_name} ${selectedRow.last_name} (${selectedRow.email})`}
                    />
                )}
            </div>
        </>
    );
};

export default WithLogin(ProviderList);
